/**
 * File: consent.ts
 * Path: packages/config/src/consent/consent.ts
 * Description: POPIA consent for the people we contact (leads and clients).
 *
 * Five states: Not asked, First message sent (asking), Said yes (date and
 * how), Existing client, Said stop (date). The older yes/no switches
 * (`marketingConsent`, `doNotContact`) stay and still decide: whichever is on
 * wins over a stored state, so ticking them in the old Details form can never
 * be out of step. The stored `consent` map only adds the date and how.
 *
 * Rules the CRM enforces from these:
 *  - one cold message (any channel) to anyone who has not said yes; a second
 *    waits for a yes (or an existing client);
 *  - "Said stop" is kept forever, even on a lost lead;
 *  - old lost or parked leads are flagged for removal after 18 months.
 */

export const CONSENT_STATES = ["not_asked", "asked", "yes", "existing_client", "stopped"] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

export const CONSENT_LABELS: Record<ConsentState, string> = {
  not_asked: "Not asked",
  asked: "First message sent, waiting for an answer",
  yes: "Said yes",
  existing_client: "Existing client",
  stopped: "Said stop",
};

export interface Consent {
  state: ConsentState;
  /** YYYY-MM-DD it was asked, agreed or stopped; null when not known. */
  on: string | null;
  /** How: "WhatsApp reply", "On a call", "Contact form"... */
  how: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What a record carries to work its consent out. */
export interface ConsentFacts {
  stored: unknown;
  doNotContact: boolean;
  marketingConsent: boolean;
  /** A first message has gone out (leads: the opener or audit email). */
  firstMessageSent: boolean;
  /** Clients are existing clients unless they said stop. */
  isClient: boolean;
}

const readStored = (value: unknown): Consent | null => {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const state = CONSENT_STATES.find((s) => s === v.state);
  if (!state) return null;
  return {
    state,
    on: typeof v.on === "string" && DATE.test(v.on) ? v.on : null,
    how: typeof v.how === "string" ? v.how.slice(0, 120) : "",
  };
};

/** The record's consent: the switches win, the stored map adds date and how. */
export const resolveConsent = (facts: ConsentFacts): Consent => {
  const stored = readStored(facts.stored);
  const keep = (state: ConsentState, fallbackHow = ""): Consent =>
    stored && stored.state === state ? stored : { state, on: null, how: fallbackHow };
  if (facts.doNotContact) return keep("stopped");
  if (facts.marketingConsent) return stored?.state === "existing_client" ? stored : keep("yes", "Recorded before consent was tracked");
  if (stored?.state === "existing_client" || (facts.isClient && stored?.state !== "yes")) return keep("existing_client");
  if (stored && (stored.state === "asked" || stored.state === "not_asked")) {
    return stored.state === "not_asked" && facts.firstMessageSent ? { state: "asked", on: null, how: "" } : stored;
  }
  return facts.firstMessageSent ? { state: "asked", on: null, how: "" } : { state: "not_asked", on: null, how: "" };
};

/** What to write on the record so the switches and the state agree. */
export const consentWrite = (consent: Consent): {
  consent: Consent;
  doNotContact: boolean;
  marketingConsent: boolean;
} => ({
  consent,
  doNotContact: consent.state === "stopped",
  marketingConsent: consent.state === "yes",
});

/** May a cold message (WhatsApp, email, SMS) go out? One is allowed, a second needs a yes. */
export type MessageBlock = "stopped" | "already-asked" | null;

export const messageBlock = (consent: Consent): MessageBlock => {
  if (consent.state === "stopped") return "stopped";
  if (consent.state === "asked") return "already-asked";
  return null;
};

export const MESSAGE_BLOCK_TEXT: Record<Exclude<MessageBlock, null>, string> = {
  stopped: "They said stop. Don't message or call them.",
  "already-asked":
    "You have already sent them a first message. Wait for a yes (or ask them to say yes) before sending another.",
};

/** Outcomes that mean a message went out to them, on WhatsApp or email. */
export const OUTBOUND_MESSAGE_OUTCOMES: readonly string[] = ["Sent a message", "Sent an email", "Sent the audit"];

export const isOutboundMessage = (type: string, outcome: string): boolean =>
  (type === "whatsapp" || type === "email") && OUTBOUND_MESSAGE_OUTCOMES.includes(outcome);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "8 Oct 2026" from YYYY-MM-DD. */
export const readableDay = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/** "Said yes on 8 Oct 2026 (WhatsApp reply)". */
export const describeConsent = (consent: Consent): string => {
  const base = CONSENT_LABELS[consent.state];
  const when = consent.on ? ` on ${readableDay(consent.on)}` : "";
  const how = consent.how ? ` (${consent.how})` : "";
  return consent.state === "not_asked" || consent.state === "existing_client" ? `${base}${how}` : `${base}${when}${how}`;
};

// ─── Where we got their details ───────────────────────────────────────

/** "Google Maps search, 8 Oct 2026": the answer to "where did you get my number?". */
export const originLine = (sourceLabel: string, createdAtMs: number | null): string => {
  if (createdAtMs === null) return sourceLabel;
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(new Date(createdAtMs));
  return `${sourceLabel}, ${readableDay(day)}`;
};

// ─── Keeping details only as long as needed ───────────────────────────

/** Lost or parked leads untouched this long are flagged for removal. */
export const RETENTION_MONTHS = 18;

export interface RetentionFacts {
  stage: string;
  parked: boolean;
  consent: Consent;
  /** Last time anyone touched it, ms; null when unknown. */
  lastTouchedAt: number | null;
}

const DAY_MS = 86_400_000;

/**
 * True when the lead is lost or parked, nobody has touched it for 18 months,
 * and it is not a "said stop" (those are kept so they are never contacted).
 */
export const isRetentionFlagged = (facts: RetentionFacts, now: number): boolean => {
  if (facts.consent.state === "stopped") return false;
  if (facts.stage !== "lost" && !facts.parked) return false;
  if (facts.lastTouchedAt === null) return false;
  return now - facts.lastTouchedAt >= RETENTION_MONTHS * 30.4375 * DAY_MS;
};

/** Client and payment records are kept this long for SARS. */
export const RECORDS_KEPT_YEARS = 5;
